import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';
import { persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { v4 as uuidv4 } from 'uuid';
import {
  ChatMessage,
  ChatUIState,
  ChatError,
  MessageStatus,
  EventType,
  ChunkAnimationType,
} from 'modules/chatbot/types/chatbot.types';
import { Sender } from 'modules/chatbot/enums/Sender';

// Constants
export const WELCOME_MESSAGE_ID = 'welcome-message';
const WELCOME_MESSAGE_CONTENT =
  'Hi, I’m SAGE! I’m here to help you understand your benefits. What can I help you with today?';
const SLICE_NAME = 'chatbot';

// API Status enum
export enum ApiStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

// Entity adapters for normalized state
const messagesAdapter = createEntityAdapter<ChatMessage>({
  sortComparer: (a, b) => a.timestamp - b.timestamp,
});

// Session-specific data structure
export interface SessionData {
  messages: EntityState<ChatMessage>;
  context: {
    benefitGuideContent: any;
    benefitGuideId?: string;
    planRecommenderContent?: any;
  };
  ui: ChatUIState;
  streaming: {
    currentStreamingId: string | null;
    isStreaming: boolean;
  };
  connection: {
    isConnected: boolean;
    lastConnectedAt: number | null;
  };
  apiStatus: ApiStatus;
  termsAccepted: boolean;
}

// State structure keyed by activeSessionId
export interface ChatState {
  activeSessionId: string | null;
  sessions: Record<string, SessionData>;
}

// Default welcome message
const WELCOME_MESSAGE: ChatMessage = {
  id: WELCOME_MESSAGE_ID,
  sender: Sender.BOT,
  content: WELCOME_MESSAGE_CONTENT,
  timestamp: Date.now(),
  status: MessageStatus.COMPLETED,
  createdAt: Date.now(),
  updatedAt: Date.now(),
};

// Transform for session-based persistence
const sessionTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: Record<string, SessionData>) => {
    const serializedSessions: Record<string, any> = {};

    Object.entries(inboundState).forEach(([sessionId, sessionData]) => {
      serializedSessions[sessionId] = {
        messages: {
          ids: sessionData.messages.ids,
          entities: sessionData.messages.entities,
        },
        termsAccepted: sessionData.termsAccepted,
        // Don't persist context, UI state, streaming, connection, or apiStatus
      };
    });

    return serializedSessions;
  },
  // Transform state being rehydrated
  (outboundState: any) => {
    const rehydratedSessions: Record<string, SessionData> = {};

    if (outboundState && typeof outboundState === 'object') {
      Object.entries(outboundState).forEach(
        ([sessionId, sessionData]: [string, any]) => {
          if (sessionData && typeof sessionData === 'object') {
            const defaultSession = createDefaultSessionData();

            rehydratedSessions[sessionId] = {
              ...defaultSession,
              messages: messagesAdapter.getInitialState({
                ids: sessionData.messages?.ids || [WELCOME_MESSAGE_ID],
                entities: sessionData.messages?.entities || {
                  [WELCOME_MESSAGE_ID]: WELCOME_MESSAGE,
                },
              }),
              // Use default context instead of persisted one
              context: defaultSession.context,
              // Persist termsAccepted across sessions
              termsAccepted: sessionData.termsAccepted ?? false,
            };

            // Ensure welcome message is always present
            if (
              !rehydratedSessions[sessionId].messages.entities[
                WELCOME_MESSAGE_ID
              ]
            ) {
              rehydratedSessions[sessionId].messages.entities[
                WELCOME_MESSAGE_ID
              ] = WELCOME_MESSAGE;
              if (
                !rehydratedSessions[sessionId].messages.ids.includes(
                  WELCOME_MESSAGE_ID
                )
              ) {
                rehydratedSessions[sessionId].messages.ids.unshift(
                  WELCOME_MESSAGE_ID
                );
              }
            }
          }
        }
      );
    }

    return rehydratedSessions;
  },
  // Define which reducers this transform gets called for
  { whitelist: ['sessions'] }
);

// Persist configuration for chatbot slice
const chatbotPersistConfig = {
  key: 'chatbot',
  storage,
  whitelist: ['activeSessionId', 'sessions'],
  transforms: [sessionTransform],
};

// Helper function to create default session data
const createDefaultSessionData = (): SessionData => ({
  messages: messagesAdapter.getInitialState({
    entities: {
      [WELCOME_MESSAGE_ID]: WELCOME_MESSAGE,
    },
    ids: [WELCOME_MESSAGE_ID],
  }),
  context: {
    benefitGuideContent: null,
    benefitGuideId: undefined,
    planRecommenderContent: null,
  },
  ui: {
    isVisible: false,
    isPending: false,
    animateTyping: { isTyping: false, messageId: null, chunkCount: 0 },
    isConnected: false,
    error: null,
    retryAttempts: 0,
    lastActivity: Date.now(),
    inputValue: '',
    isMinimized: false,
    isFullScreen: false,
    showNewChatOverlay: false,
    showPreparingOverlay: false,
    initializationStatus: ApiStatus.IDLE,
    initializationError: null as string | null,
  },
  streaming: {
    currentStreamingId: null,
    isStreaming: false,
  },
  connection: {
    isConnected: false,
    lastConnectedAt: null,
  },
  apiStatus: ApiStatus.IDLE,
  termsAccepted: false,
});

// Helper function to ensure session exists
const ensureSession = (state: ChatState, sessionId: string): SessionData => {
  if (!state.sessions[sessionId]) {
    state.sessions[sessionId] = createDefaultSessionData();
  }
  return state.sessions[sessionId];
};

// Initial state with session-based structure
const initialState: ChatState = {
  activeSessionId: null,
  sessions: {},
};

// Utility function to generate message ID using UUID
const generateMessageId = (): string => uuidv4();

const chatSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    // UI State Management
    setVisible: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.isVisible = action.payload;
      session.ui.lastActivity = Date.now();
    },

    setPending: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.isPending = action.payload;
      session.ui.lastActivity = Date.now();
    },

    setTypingAnimation: (state, action: PayloadAction<ChunkAnimationType>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.animateTyping = action.payload;
      session.ui.lastActivity = Date.now();
    },

    setTypingChunkCount: (state, action: PayloadAction<number>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.animateTyping.chunkCount = action.payload;
      session.ui.lastActivity = Date.now();
    },

    setError: (state, action: PayloadAction<ChatError | null>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.error = action.payload;
      if (action.payload) {
        session.ui.retryAttempts += 1;
      } else {
        session.ui.retryAttempts = 0;
      }
    },

    clearError: (state) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.error = null;
      session.ui.retryAttempts = 0;
    },

    setShowNewChatOverlay: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.showNewChatOverlay = action.payload;
    },

    setShowPreparingOverlay: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.showPreparingOverlay = action.payload;
    },

    setInitializationStatus: (state, action: PayloadAction<ApiStatus>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.initializationStatus = action.payload;
    },

    setInitializationError: (state, action: PayloadAction<string | null>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.initializationError = action.payload;
    },

    setFullScreen: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.ui.isFullScreen = action.payload;
      session.ui.lastActivity = Date.now();
    },

    setContext: (state, action: PayloadAction<any>) => {
      const sessionId = action.payload.benefitGuideContent.urlHash;
      state.activeSessionId = sessionId;
      const session = ensureSession(state, sessionId);
      session.context = action.payload;
    },

    setPlanRecommenderContext: (
      state,
      action: PayloadAction<{ sessionId: string; planRecommenderContent: any }>
    ) => {
      const { sessionId, planRecommenderContent } = action.payload;
      const session = ensureSession(state, sessionId);
      session.context.planRecommenderContent = planRecommenderContent;
    },

    // Message Management
    addMessage: (
      state,
      action: PayloadAction<
        Omit<ChatMessage, 'id' | 'timestamp' | 'createdAt' | 'updatedAt'>
      >
    ) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      const now = Date.now();
      const message: ChatMessage = {
        ...action.payload,
        id: generateMessageId(),
        timestamp: now,
        createdAt: now,
        updatedAt: now,
      };

      messagesAdapter.addOne(session.messages, message);
      session.ui.lastActivity = Date.now();

      // Set streaming ID only for bot messages
      if (message.sender === Sender.BOT) {
        session.streaming.currentStreamingId = message.id;
        session.streaming.isStreaming = true;
      }
    },

    setActiveSessionId: (state, action: PayloadAction<string>) => {
      state.activeSessionId = action.payload;
    },

    setNewChat: (state) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      // Clear current messages except welcome message
      messagesAdapter.removeAll(session.messages);
      messagesAdapter.addOne(session.messages, WELCOME_MESSAGE);
      session.ui.isPending = false;
      session.ui.error = null;
      session.ui.retryAttempts = 0;
      session.ui.lastActivity = Date.now();
      session.streaming.currentStreamingId = null;
      session.streaming.isStreaming = false;
      session.apiStatus = ApiStatus.IDLE;
      // Reset terms acceptance for new chat
      session.termsAccepted = false;
    },

    // Terms of Service Management
    setTermsAccepted: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.termsAccepted = action.payload;
    },

    // Update message content (for streaming)
    updateMessage: (
      state,
      action: PayloadAction<{
        content: string;
        eventType: EventType;
        sources?: any[];
      }>
    ) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      const { content, eventType, sources } = action.payload;
      const message =
        session.messages.entities[session.streaming.currentStreamingId!];

      if (!message) return;

      // Helper functions to reduce code duplication
      const clearStatusSteps = () => {
        if (message.metadata?.steps) {
          message.metadata.steps = [];
        }
      };

      const appendContentWithSpacing = (newContent: string) => {
        if (message.metadata?.eventType === EventType.STATUS) {
          // First content after status - replace status content
          message.content = newContent;
        } else {
          // Subsequent content - append with space if needed
          const needsSpace =
            message.content &&
            !message.content.endsWith(' ') &&
            !newContent.startsWith(' ');
          message.content += (needsSpace ? ' ' : '') + newContent;
        }
      };

      const appendActionsMessage = (newContent: string) => {
        message.content += newContent;
      };

      const endStreaming = (status: MessageStatus, apiStatus: ApiStatus) => {
        message.status = status;
        session.streaming.currentStreamingId = null;
        session.streaming.isStreaming = false;
        session.apiStatus = apiStatus;
      };

      // Handle different event types with switch case
      switch (eventType) {
        case EventType.STATUS:
          // For status messages, add to steps array (FIFO queue)
          if (!message.metadata) {
            message.metadata = {};
          }
          if (!message.metadata.steps) {
            message.metadata.steps = [];
          }
          message.metadata.steps.push(content);
          message.status = MessageStatus.STREAMING;
          break;

        case EventType.CHUNK:
        case EventType.RESPONSE:
          // Clear status steps and append content with proper spacing
          clearStatusSteps();
          appendContentWithSpacing(content);
          message.status = MessageStatus.STREAMING;
          break;

        case EventType.ACTION:
          // Clear status steps and append content directly
          clearStatusSteps();
          appendActionsMessage(content);
          message.status = MessageStatus.STREAMING;
          break;

        case EventType.SOURCES:
          // Clear status steps and append content directly
          clearStatusSteps();
          message.content += content;
          message.status = MessageStatus.STREAMING;
          break;

        case EventType.COMPLETE:
          // Clear status steps, set sources if provided, and mark as completed
          clearStatusSteps();
          if (sources) {
            message.sources = sources;
          }
          endStreaming(MessageStatus.COMPLETED, ApiStatus.SUCCESS);
          break;

        case EventType.ERROR:
          // Clear status steps, set error content, and mark as failed
          clearStatusSteps();
          // For error events, display the error message in red
          message.content =
            content || 'An error occurred while processing your request.';
          endStreaming(MessageStatus.FAILED, ApiStatus.ERROR);
          break;

        default:
          // For other types, append content directly
          message.content += content;
          break;
      }

      if (eventType) {
        message.metadata = { ...message.metadata, eventType };
      }
      message.updatedAt = Date.now();
    },

    // Streaming state management
    setCurrentStreamingId: (state, action: PayloadAction<string | null>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.streaming.currentStreamingId = action.payload;
      session.streaming.isStreaming = action.payload !== null;
    },

    clearStreaming: (state) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.streaming.currentStreamingId = null;
      session.streaming.isStreaming = false;
    },

    setConnectionState: (state, action: PayloadAction<boolean>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.connection.isConnected = action.payload;
      if (action.payload) {
        session.connection.lastConnectedAt = Date.now();
      }
    },

    // API Status Management
    setApiStatus: (state, action: PayloadAction<ApiStatus>) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      session.apiStatus = action.payload;
    },

    // Clear all streaming state
    resetStreamingState: (state) => {
      if (!state.activeSessionId) return;
      const session = ensureSession(state, state.activeSessionId);
      // Clear status steps when resetting streaming state
      const currentStreamingId = session.streaming.currentStreamingId;
      if (currentStreamingId) {
        const message = session.messages.entities[currentStreamingId];
        if (message?.metadata?.steps) {
          message.metadata.steps = [];
        }
      }

      session.streaming.currentStreamingId = null;
      session.streaming.isStreaming = false;
      session.apiStatus = ApiStatus.IDLE;
    },
  },
});

// Export actions
export const {
  setActiveSessionId,
  setVisible,
  setPending,
  setTypingAnimation,
  setTypingChunkCount,
  setError,
  clearError,
  setShowNewChatOverlay,
  setShowPreparingOverlay,
  setInitializationStatus,
  setInitializationError,
  setFullScreen,
  setContext,
  setPlanRecommenderContext,
  addMessage,
  setNewChat,
  updateMessage,
  setCurrentStreamingId,
  clearStreaming,
  setConnectionState,
  setApiStatus,
  resetStreamingState,
  setTermsAccepted,
} = chatSlice.actions;

// Helper selector to get current session
const selectCurrentSession = (state: {
  chatBot: ChatState;
}): SessionData | null => {
  const { activeSessionId, sessions } = state.chatBot;
  if (!activeSessionId || !sessions[activeSessionId]) return null;
  return sessions[activeSessionId];
};

// Export selectors using entity adapters
export const {
  selectAll: selectAllMessages,
  selectById: selectMessageById,
  selectIds: selectMessageIds,
} = messagesAdapter.getSelectors((state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.messages || messagesAdapter.getInitialState();
});

// Core selectors
export const selectActiveSessionId = (state: { chatBot: ChatState }) =>
  state.chatBot.activeSessionId;

export const selectChatUIState = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui || createDefaultSessionData().ui;
};

export const selectConnectionState = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.connection || createDefaultSessionData().connection;
};

// Streaming selectors
export const selectStreamingState = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.streaming || createDefaultSessionData().streaming;
};

export const selectCurrentStreamingId = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.streaming.currentStreamingId || null;
};

export const selectIsStreaming = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.streaming.isStreaming || false;
};

// API status selectors
export const selectApiStatus = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.apiStatus || ApiStatus.IDLE;
};

export const selectIsApiLoading = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.apiStatus === ApiStatus.LOADING;
};

export const selectHasApiError = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.apiStatus === ApiStatus.ERROR;
};

// UI state selectors
export const selectIsVisible = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.isVisible || false;
};

export const selectIsPending = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.isPending || false;
};

export const selectIsTypingAnimate = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.animateTyping || null;
};

export const selectChatError = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.error || null;
};

export const selectShowNewChatOverlay = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.showNewChatOverlay || false;
};

export const selectShowPreparingOverlay = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.showPreparingOverlay || false;
};

export const selectInitializationStatus = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.initializationStatus || ApiStatus.IDLE;
};

export const selectInitializationError = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.initializationError || null;
};

export const selectIsFullScreen = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.ui.isFullScreen || false;
};

// Terms of Service selectors
export const selectTermsAccepted = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.termsAccepted || false;
};

export const selectContext = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return (
    session?.context || {
      benefitGuideContent: null,
      benefitGuideId: undefined,
      planRecommenderContent: null,
    }
  );
};

// Check if context has been initialized (has benefitGuideContent)
export const selectHasContext = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.context?.benefitGuideContent !== null;
};

// Get the stored benefitGuideId from context
export const selectContextBenefitGuideId = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.context?.benefitGuideId;
};

// Get the plan recommender content from context
export const selectPlanRecommenderContext = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  return session?.context?.planRecommenderContent || null;
};

// Legacy selectors for backward compatibility
export const selectMessages = selectAllMessages;
export const selectConversationId = selectActiveSessionId;
export const selectActiveSessionMessages = selectAllMessages;

// Check if last bot message is completed (input should be enabled)
export const selectIsInputEnabled = (state: { chatBot: ChatState }) => {
  const messages = selectAllMessages(state);

  // Find the last bot message
  const lastBotMessage = messages
    .slice()
    .reverse()
    .find((message) => message.sender === Sender.BOT);

  // If no bot message exists, input is enabled
  if (!lastBotMessage) return true;

  // Input is enabled if last bot message is completed OR failed (to allow retry after errors)
  return (
    lastBotMessage.status === MessageStatus.COMPLETED ||
    lastBotMessage.status === MessageStatus.FAILED
  );
};

// Selector to get current streaming steps
export const selectCurrentStreamingSteps = (state: { chatBot: ChatState }) => {
  const session = selectCurrentSession(state);
  const currentStreamingId = session?.streaming.currentStreamingId;
  if (!currentStreamingId || !session) return [];

  const message = session.messages.entities[currentStreamingId];
  return message?.metadata?.steps || [];
};

// Create persisted reducer
const persistedChatbotReducer = persistReducer(
  chatbotPersistConfig,
  chatSlice.reducer
);

// Export persisted reducer as default
export default persistedChatbotReducer;
